import { StyleSheet, Platform } from 'react-native'
import type { Theme } from '../../../src/theme/getTheme'

// Design-system colors for the date strip (dark purple palette)
const C = {
  cardBg: '#1A1233',
  border: '#2A1F44',
  primary: '#A78BFA',
  gold: '#FCD34D',
  textDim: '#6B7280',
  textDefault: '#F7FAFC',
  selectedText: '#111827',
  selectedTextDim: 'rgba(17,24,39,0.7)',
} as const

export const getCalendarStripStyles = (_theme: Theme) =>
  StyleSheet.create({
    container: {
      width: '100%',
      alignSelf: 'stretch',
      backgroundColor: 'transparent',
      paddingBottom: 10,
    },
    calendarContent: {
      width: '100%',
      minWidth: 0,
    },
    monthRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: 12,
    },
    monthLabel: {
      fontSize: 16,
      fontWeight: '700' as const,
      color: C.textDefault,
    },
    weekRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      gap: 6,
      width: '100%',
    },
    weekNavButton: {
      paddingVertical: 8,
      paddingHorizontal: 2,
      alignSelf: 'center',
      ...(Platform.OS === 'web' && { cursor: 'pointer' as const }),
    },
    weekNavButtonDisabled: {
      opacity: 0.3,
      ...(Platform.OS === 'web' && { cursor: 'default' as never }),
    },
    weekNavButtonPressed: {
      opacity: 0.6,
    },
    // Each day is a pill button
    dayCell: {
      flex: 1,
      alignItems: 'center',
      paddingTop: 8,
      paddingBottom: 6,
      paddingHorizontal: 4,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: C.border,
      minWidth: 36,
      ...(Platform.OS === 'web' && { cursor: 'pointer' as const }),
    },
    dayCellSelected: {
      backgroundColor: C.primary,
      borderWidth: 0,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 2px 12px rgba(167,139,250,0.4)' as const }
        : {}),
    },
    dayCellDisabled: {
      opacity: 0.4,
    },
    dayCellLocked: {
      opacity: 0.3,
    },
    dayName: {
      fontSize: 11,
      fontWeight: '700' as const,
      letterSpacing: 0.5,
      color: C.textDim,
      marginBottom: 2,
    },
    dayNameSelected: {
      color: C.selectedTextDim,
    },
    dayNameToday: {
      color: C.gold,
      fontSize: 9,
      letterSpacing: 0,
    },
    dayNameDisabled: {
      color: C.textDim,
    },
    dayNameLocked: {
      color: C.textDim,
    },
    dayNumberText: {
      fontSize: 18,
      fontWeight: '700' as const,
      color: C.textDefault,
      lineHeight: 22,
      marginBottom: 2,
    },
    dayNumberTextSelected: {
      color: C.selectedText,
    },
    dayNumberTextDisabled: {
      color: C.textDim,
    },
    dayNumberTextLocked: {
      color: C.textDim,
    },
    dayDot: {
      width: 4,
      height: 4,
      borderRadius: 9999,
      backgroundColor: C.primary,
      opacity: 0.7,
      marginTop: 2,
    },
    dayDotSelected: {
      backgroundColor: C.selectedText,
      opacity: 0.5,
    },
    dayDotHidden: {
      opacity: 0,
    },
    // Pick count on a date (create list, specs/challenge-flow-redesign AC-3.9). Gold reads on both
    // the purple selected pill and the dark unselected one.
    dayBadge: {
      position: 'absolute',
      top: -6,
      right: -6,
      minWidth: 16,
      height: 16,
      paddingHorizontal: 3,
      borderRadius: 9999,
      backgroundColor: C.gold,
      alignItems: 'center',
      justifyContent: 'center',
    },
    dayBadgeText: {
      fontSize: 10,
      fontWeight: '800' as const,
      color: C.selectedText,
    },
  })
